import { useRef, useState, useEffect, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
// Formik components
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import MuiDialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";
import FormField from "../../../FormField";
import validations from "./schemas/validations";
import event from "./schemas/event";
import DeleteScheduleMenuItem from "../DeleteSchedulteMenuItem";
import { createSchedule, updateSchedule, setSaveScheduleSuccessReset } from "redux/actions/flowDesigner/calendarActionCreator";
import { openDiscardDialog } from "redux/actions/flowDesigner/commonActionCreator";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import SoftTypography from "components/SoftTypography";
import { setCalendarFlows } from "redux/actions/flowDesigner/calendarActionCreator";
import DatePicker from "react-datepicker";
import SoftInput from "components/SoftInput";
import "./styles.css";
import { usePortalFlows } from "hooks/usePortalFlows";
import { RecurrencePicker } from "./components/RecurrencePicker";
import { ThemedXIcon } from "assets/CXFIcons/Static";
import { createRepeatValue } from "helpers/scheduler";
import ProtectedButton from "authentication/ProtectedButton";
import { PERMISSIONS } from "constants/portal";

const Dialog = styled(MuiDialog)(() => ({
   "& .MuiDialog-paper": {
      display: "flex",
      maxHeight: "calc(100dvh - 64px)",
      overflow: "hidden"
   }
}));

const DialogContent = styled(MuiDialogContent)(() => ({
   minHeight: 0,
   overflowY: "auto",
   padding: "16px 20px"
}));

const SchedulerDialog = ({ open, closeDialog, selectedDate, selectedEvent }) => {
   const { user } = useAuth0();
   const { t } = useTranslation("flowDesigner");
   const { flows: flowsData, saveScheduleFailed, saveScheduleSuccess } = useSelector((store) => store?.designer?.calendar);
   const dispatch = useDispatch();
   const [submitting, setSubmitting] = useState(false);
   const [isPausedEvent, setIsPausedEvent] = useState(false);
   const { flows: portalFlows } = usePortalFlows();
   const [startDate, setStartDate] = useState(new Date());
   const [endDate, setEndDate] = useState();
   const [isDateInvalid, setIsDateInvalid] = useState(false);
   const formRef = useRef(null);
   const {
      formId,
      formField: { eventName, description, repeat, repetition }
   } = event;

   const getInitialValues = () => {
      return {
         [eventName.name]: selectedEvent?.title || "",
         [description.name]: selectedEvent?.extendedProps?.description || selectedEvent?.description || "",
         [repeat.name]: selectedEvent?.extendedProps?.repeat || selectedEvent?.repeat || "not_repeat",
         [repetition.name]: selectedEvent?.extendedProps?.repetition || selectedEvent?.repetition || ""
      };
   };

   const resetForm = () => {
      formRef.current?.resetForm();
   };

   const handleOpenDiscardDialog = () => {
      dispatch(openDiscardDialog());
   };

   const resetFormAndCloseDialog = () => {
      resetForm();
      closeDialog();
   };

   const checkDirtyAndClose = () => {
      if (formRef.current.dirty) {
         handleOpenDiscardDialog();
         return;
      }
      resetFormAndCloseDialog();
   };

   const handleSaveSubmit = async (values, actions) => {
      setSubmitting(true);
      actions.setSubmitting(false);

      try {
         const { eventName, description, repeat, repetition } = values;

         const input = {
            created_by_id: user?.cxfUserId,
            description: description,
            end_date: endDate ? endDate.toISOString() : null,
            name: eventName,
            repeat_type: repeat,
            start_date: startDate.toISOString(),
            status: isPausedEvent ? "Paused" : repeat === "not_repeat" ? "Active" : "Recurring",
            repetition: repeat !== "not_repeat" ? `${repetition}` : ""
         };

         if (selectedEvent?.id) {
            dispatch(updateSchedule(user?.stripeId, selectedEvent?.id, input));
         } else {
            dispatch(createSchedule(user?.stripeId, input));
         }
      } catch (err) {
         console.log(err);
         setSubmitting(false);
      }
   };

   const handleSubmit = (event) => {
      if (isDateInvalid) return;

      event.preventDefault();
      formRef.current.submitForm();
   };

   const handleLinkClick = () => {
      const linkPopup = document.querySelector(".ql-tooltip");
      if (linkPopup) {
         let left = linkPopup.style.left;
         left = left.replace("px", "");
         left = Number(left);
         if (left < 0) {
            left = 0;
         }
         linkPopup.style.left = left;
      }
   };

   const handleFilterStartTime = (startTime) => {
      if (!endDate) return true;

      const startTimeDate = new Date(startTime);
      return startTimeDate.getTime() <= endDate.getTime();
   };

   const handleFilterEndTime = (endTime) => {
      const endTimeDate = new Date(endTime);
      return endTimeDate.getTime() >= startDate.getTime();
   };

   const recuringEventButtons = () => {
      return isPausedEvent ? (
         <SoftButton variant="text" color="primary" startIcon={<PlayArrowIcon />} onClick={() => setIsPausedEvent(false)}>
            Enable
         </SoftButton>
      ) : (
         <SoftButton variant="text" color="primary" startIcon={<PauseIcon />} onClick={() => setIsPausedEvent(true)}>
            Disable
         </SoftButton>
      );
   };

   useEffect(() => {
      if (saveScheduleSuccess) {
         setSubmitting(false);
         dispatch(setSaveScheduleSuccessReset());
         closeDialog();
      }
   }, [saveScheduleSuccess]);

   useEffect(() => {
      if (saveScheduleFailed) {
         setSubmitting(false);
      }
   }, [saveScheduleFailed]);

   useEffect(() => {
      if (open) {
         let linkTrigger;

         resetForm();
         setSubmitting(false);

         setTimeout(() => {
            let element = document.querySelector(".ql-bold");

            if (element) {
               element?.setAttribute("title", "Bold");
               element = document.querySelector(".ql-italic");
               element.setAttribute("title", "Italic");
               element = document.querySelector(".ql-underline");
               element.setAttribute("title", "Underline");
               element = document.querySelector(".ql-link");
               element.setAttribute("title", "Link");
               element = document.querySelector(".ql-list[value='ordered']");
               element.setAttribute("title", "Numbered list");
               element = document.querySelector(".ql-list[value='bullet']");
               element.setAttribute("title", "Bulleted list");
               element = document.querySelector(".ql-clean");
               element.setAttribute("title", "Remove formatting");
               element = document.querySelector(".ql-formats");
               element.setAttribute("title", "");
            }

            linkTrigger = document.querySelector(".ql-link");
            if (linkTrigger) {
               linkTrigger.addEventListener("click", handleLinkClick);
            }
         }, 1000);

         setIsPausedEvent(selectedEvent?.extendedProps?.status === "Paused" || selectedEvent?.status === "Paused");

         return () => {
            if (linkTrigger) {
               linkTrigger.removeEventListener("click", handleLinkClick);
            }
         };
      }
   }, [open]);

   useEffect(() => {
      if (!portalFlows?.length || flowsData?.length) return;

      const mappedFlows = portalFlows
         .filter((f) => !f.trashed && f.shared_status !== "test")
         .map((flow) => ({
            value: flow.id,
            label: flow.name
         }));
      dispatch(setCalendarFlows(mappedFlows));
   }, [portalFlows]);

   useEffect(() => {
      if (!endDate) return;

      setIsDateInvalid(startDate > endDate);
   }, [startDate, endDate]);

   useEffect(() => {
      if (open && !selectedEvent) {
         setStartDate(new Date());
         setEndDate(undefined);
      }
   }, [open, selectedEvent]);

   useEffect(() => {
      if (selectedEvent) {
         setStartDate(new Date(selectedEvent.start));

         if (selectedEvent.end) {
            setEndDate(new Date(selectedEvent.end));
         }

         return;
      }

      if (selectedDate) {
         setStartDate(selectedDate);
         const selectedDateTime = new Date(selectedDate);
         selectedDateTime.setTime(selectedDateTime.getTime() + 30 * 60 * 1000);
         setEndDate(selectedDateTime);
      }
   }, [selectedEvent, selectedDate]);

   return (
      <>
         <Dialog open={open} fullWidth={true} maxWidth="md">
            <DialogTitle data-testid="form-title">{selectedEvent ? t("common.edit_schedule") : t("common.add_new_schedule")}</DialogTitle>
            <DialogContent dividers className="scheduler-content">
               {selectedEvent && (
                  <SoftBox>
                     <DeleteScheduleMenuItem handleClose={closeDialog} selectedEvent={selectedEvent} flows={portalFlows} />
                     <ProtectedButton permissionName={PERMISSIONS.PAUSE_SCHEDULES} isOwner={selectedEvent?.created_by_id === user?.cxfUserId}>
                        {recuringEventButtons()}
                     </ProtectedButton>
                  </SoftBox>
               )}
               <Formik initialValues={getInitialValues()} validationSchema={validations} onSubmit={handleSaveSubmit} innerRef={formRef} enableReinitialize>
                  {({ errors, touched, values, setFieldValue }) => {
                     return (
                        <Form id={formId} autoComplete="off">
                           <FormField
                              id="eventName"
                              type={eventName.type}
                              label={eventName.label}
                              name={eventName.name}
                              error={errors.eventName && touched.eventName}
                              placeholder={eventName.placeholder ? t(eventName.placeholder) : ""}
                              autoFocus
                              required={true}
                              onChange={(e)=> {
                                 setFieldValue(eventName.name, e.target.value);
                              }}
                           />
                           <SoftBox
                              className="date-pickers"
                              sx={{
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "space-between",
                                 position: "relative"
                              }}
                           >
                              <SoftBox sx={{ flexBasis: "48%" }}>
                                 <SoftTypography component="label" variant="caption" fontWeight="bold" textTransform="capitalize">
                                    Starting date and time <span style={{ color: "#ff667c" }}>*</span>
                                 </SoftTypography>
                                 <DatePicker
                                    className="scheduler__datetime full"
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={30}
                                    timeCaption="Time"
                                    dateFormat="MMMM d, yyyy h:mm aa zzzz"
                                    customInput={<SoftInput />}
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="scroll"
                                    maxDate={endDate ? endDate : undefined}
                                    filterTime={handleFilterStartTime}
                                    popperPlacement="bottom-start"
                                 />

                                 {isDateInvalid && (
                                    <SoftTypography
                                       sx={{
                                          color: "#fd5e5e",
                                          fontSize: "12px"
                                       }}
                                    >
                                       Invalid date
                                    </SoftTypography>
                                 )}
                              </SoftBox>
                              <ArrowRightAltOutlinedIcon fontSize="medium" sx={{ marginTop: "18px" }} />
                              <SoftBox sx={{ flexBasis: "48%" }}>
                                 <SoftTypography component="label" variant="caption" fontWeight="bold" textTransform="capitalize">
                                    Ending date and time
                                 </SoftTypography>
                                 <DatePicker
                                    className="scheduler__datetime full"
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={30}
                                    timeCaption="Time"
                                    dateFormat="MMMM d, yyyy h:mm aa zzzz"
                                    customInput={<SoftInput />}
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="scroll"
                                    minDate={startDate}
                                    filterTime={handleFilterEndTime}
                                    popperPlacement="bottom-start"
                                 />
                                 {isDateInvalid && (
                                    <SoftTypography
                                       sx={{
                                          color: "#fd5e5e",
                                          fontSize: "12px"
                                       }}
                                    >
                                       Invalid date
                                    </SoftTypography>
                                 )}
                                 {endDate && (
                                    <button
                                       style={{
                                          background: "none",
                                          border: "none",
                                          cursor: "pointer",
                                          inset: "58% 1rem auto auto",
                                          position: "absolute",
                                          fontWeight: "600"
                                       }}
                                       type="button"
                                       onClick={() => setEndDate(null)}
                                    >
                                       <ThemedXIcon />
                                    </button>
                                 )}
                              </SoftBox>
                           </SoftBox>
                           <Grid container spacing={4}>
                              <Grid size={{ xs: 6 }}>
                                 <FormField
                                    type={repeat.type}
                                    label={repeat.label}
                                    name={repeat.name}
                                    value={createRepeatValue(values.repeat)}
                                    error={Boolean(errors[repeat.name])}
                                    placeholder={repeat.placeholder ? repeat.placeholder : ""}
                                    options={repeat.options}
                                 />
                              </Grid>
                           </Grid>
                           {values.repeat && <RecurrencePicker errors={errors} values={values} setFieldValue={setFieldValue} repetition={repetition} endDate={endDate} startDate={startDate} />}
                           <FormField
                              className="max-h-240"
                              type={description.type}
                              label={description.label}
                              name={description.name}
                              error={errors[description.name] && touched[description.name]}
                              placeholder={description.placeholder ? t(description.placeholder) : ""}
                              handleLinkClick={handleLinkClick}
                              isMultiline
                           />
                        </Form>
                     );
                  }}
               </Formik>
            </DialogContent>
            <DialogActions>
               <SoftButton variant="outlined" color="info" onClick={checkDirtyAndClose}>
                  {t("buttons.cancel")}
               </SoftButton>
               <SoftButton disabled={submitting} color="primary" onClick={handleSubmit}>
                  {t("buttons.save")}
               </SoftButton>
            </DialogActions>
         </Dialog>
         <DiscardChangesDialog onAccept={resetFormAndCloseDialog} isParentDialogVisible={open} />
      </>
   );
};

export default memo(SchedulerDialog);
